import { useCallback, useState } from 'react'

import { Region } from 'clickNgo/configs/geo'
import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import { IContact } from 'clickNgo/entities/contact'
import {
  fetchContactRequestById,
  TSearchPortalContact,
  useActivateContactMutation,
  useRelinkContactMutation,
} from 'clickNgo/entities/contact/api'
import { fetchPartnerHierarchy } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ConfirmDialog } from 'cng-common/components/confirmDialog'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'

import { ContactRelinkingValidationMessage } from '../contactRelinking'

import { contactReactivationBlockTranslations } from './translations'

import styles from './ContactDuplicateReactivation.module.scss'

interface Props {
  partner: IPartner
  duplicate: TSearchPortalContact
  onReactivationComplete: (contact: IContact) => void
  source: ProfilingRequestSource
}

type LoadingState = {
  isLoading: boolean
  message?: string
}

const initialLoadingState: LoadingState = { isLoading: false }

export const ContactDuplicateReactivation = ({
  partner,
  duplicate,
  onReactivationComplete,
  source,
}: Props) => {
  const [activateContact] = useActivateContactMutation()
  const [relinkContact] = useRelinkContactMutation()
  const [relinkReason, setRelinkReason] = useState<string>('')
  const [serviceErrorMessage, setServiceErrorMessage] = useState<string>('')
  const [relinkingValidationMessage, setRelinkingValidationMessage] = useState<string>('')
  const [{ isLoading, message }, setLoadingState] = useState<LoadingState>(initialLoadingState)

  const reactivateContact = useCallback(async () => {
    setServiceErrorMessage('')
    setLoadingState({ isLoading: true, message: 'Reactivating contact...' })

    try {
      const response = await activateContact({
        email: duplicate.emailAddress,
        targetPartnerId: partner.partnerId,
        source,
      }).unwrap()

      // Fetch full contact details after reactivation
      const reactivatedContact = await fetchContactRequestById(response.contactId)

      if (reactivatedContact) {
        onReactivationComplete(reactivatedContact)
      } else {
        setServiceErrorMessage(`Unable to fetch reactivated contact, try to refreash the page`)
      }
    } catch (error) {
      setServiceErrorMessage(`ERROR: ${error}`)
      return
    } finally {
      setLoadingState(initialLoadingState)
    }
  }, [activateContact, duplicate.emailAddress, onReactivationComplete, partner.partnerId, source])

  const handleReactivateClick = useCallback(async () => {
    if (partner.partnerId !== duplicate.partnerId || !duplicate.locationId) {
      if (!duplicate.locationId) {
        setRelinkReason(contactReactivationBlockTranslations.relinkNeeded.reason.noLocation)
      } else {
        setRelinkReason(contactReactivationBlockTranslations.relinkNeeded.reason.differentPartner)
      }
      return
    }

    reactivateContact()
  }, [duplicate.locationId, duplicate.partnerId, partner.partnerId, reactivateContact])

  const handleContactRelinkingValidationClose = useCallback(() => {
    setRelinkingValidationMessage('')
  }, [])

  const handleRelinkNeededClose = useCallback(() => {
    setRelinkReason('')
  }, [])

  const handleRelinkConfirm = useCallback(async () => {
    setServiceErrorMessage('')
    setRelinkReason('')
    setLoadingState({ isLoading: true, message: 'Relink contact...' })

    try {
      // Validations before relink if needed
      if (duplicate.locationId && partner.partnerId !== duplicate.partnerId) {
        setLoadingState({ isLoading: true, message: 'Validating contact...' })

        // Validations for APJ region - target partner must be in hierarchy
        if (partner.region === Region.APJ) {
          const partnerHierarchy = await fetchPartnerHierarchy({
            partnerId: partner.topPartnerId,
          })

          const isTargetPartnerInHierarchy = partnerHierarchy.some(
            (p) => p.partnerId === duplicate.partnerId
          )

          if (!isTargetPartnerInHierarchy) {
            setRelinkingValidationMessage(`Target partner is not in hierarchy`)
            return
          }
        }

        // Validation for country match
        if (partner.countryCode !== duplicate.countryOfBusiness) {
          setRelinkingValidationMessage(
            `Cannot relink contact: country mismatch between partner and contact`
          )
          return
        }
      }
      // Proceed to relink

      await relinkContact({
        email: duplicate.emailAddress,
        targetPartnerId: partner.partnerId,
        source,
        comment: 'RELINK BEFORE REACTIVATION',
      }).unwrap()

      await reactivateContact()
    } catch (error) {
      setServiceErrorMessage(`ERROR: ${error}`)
      return
    } finally {
      setLoadingState(initialLoadingState)
    }
  }, [
    duplicate.countryOfBusiness,
    duplicate.emailAddress,
    duplicate.locationId,
    duplicate.partnerId,
    partner.countryCode,
    partner.partnerId,
    partner.region,
    partner.topPartnerId,
    reactivateContact,
    relinkContact,
    source,
  ])

  return (
    <div className={styles.container}>
      <RenderWhen condition={Boolean(serviceErrorMessage)}>
        <InfoBlock
          text={serviceErrorMessage}
          type={InfoBlockType.Danger}
          rounded={InfoBlockRounded.Small}
          hasIcon
        />
      </RenderWhen>
      <Flex justifyContent={'flex-end'}>
        <LoaderOverlay isVisible={isLoading} message={message} />
        <Button color={'success'} onClick={handleReactivateClick}>
          <Translation text={contactReactivationBlockTranslations.reactivate} />
        </Button>
        <ContactRelinkingValidationMessage
          text={relinkingValidationMessage}
          onClose={handleContactRelinkingValidationClose}
        />
      </Flex>
      <ConfirmDialog
        confirmBtnText={contactReactivationBlockTranslations.relinkNeeded.confirm}
        rejectBtnText={contactReactivationBlockTranslations.relinkNeeded.reject}
        content={relinkReason}
        isVisible={Boolean(relinkReason)}
        title={contactReactivationBlockTranslations.relinkNeeded.title}
        onReject={handleRelinkNeededClose}
        onConfirm={handleRelinkConfirm}
      />
    </div>
  )
}

ContactDuplicateReactivation.displayName = 'ContactDuplicateReactivation'
